// src/utils/portfolio.js
//
// The project → assets layer.
//
// A project is a portfolio: one client, company or SPV owning several assets
// (sites). Each asset carries exactly what the platform called "the project
// config" until now: site, PV, wind, BESS, grid connection, bidding zone,
// feasibility assumptions. A feasibility study is done per asset; market
// operations run across the commissioned assets of the project.
//
// Storage stays backward compatible with the backend, which reads the flat
// config of ONE asset from `project.config` (daily optimisation, feasibility
// runs, bid schedules all key on it). So the ACTIVE asset's config is stored
// at the root exactly as before, and the portfolio itself lives under
// `config.portfolio`:
//
//   {
//     ...<active asset config, flat, as today>,
//     portfolio: {
//       schemaVersion: 1,
//       project: { description, owner, company, notes, accessRoute, aggregatorName },
//       assets: [ { id, name, status, notes, config } ],   // active: config = null (it is the root)
//       activeAssetId
//     }
//   }
//
// A stored config without `portfolio` is a legacy single-asset project and is
// wrapped as a project with one asset on load; nothing about it changes.

import { DEFAULT_CONFIG } from "../components/config/defaultConfig";
import { NEW_HORIZON_SETTINGS } from "../components/config/horizonShading";

import { applyAssetType, assetTypeDefinition } from "./projectAssets";

export const PORTFOLIO_SCHEMA_VERSION = 1;
// Scoped by project, matching the asset created by the database migration.
export const LEGACY_ASSET_ID = "legacy-asset-1";

export const ASSET_STATUSES = ["study", "operating"];

export const ASSET_STATUS_META = {
  study: { label: "Under study", color: "info", hint: "Feasibility study in progress; not yet built." },
  operating: { label: "Operating", color: "success", hint: "Built and energised; eligible for Market Operations, subject to permissions and readiness checks." },
};

export function normalizeAssetStatus(status) {
  return ({ concept: "study", commissioned: "operating" })[status] || status;
}

export function assetStatusMeta(status) {
  return ASSET_STATUS_META[normalizeAssetStatus(status)] || { label: "Needs review", color: "default", hint: "Choose the asset's lifecycle status." };
}

/** Statuses that make an asset part of market operations. */
export const OPERATING_STATUSES = ["operating"];

const clone = (v) => (typeof structuredClone === "function" ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

export function newAssetId() {
  return `asset-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function emptyProjectMeta() {
  return { description: "", owner: "", company: "", notes: "", accessRoute: "", aggregatorName: "" };
}

/** A fresh asset config: the platform defaults. `projectName` (the description the backend reads) is project-level and set at save time. */
export function newAssetConfig(description, systemType = "Hybrid") {
  const cfg = clone(DEFAULT_CONFIG);
  cfg.panelOrientation = { ...cfg.panelOrientation, ...NEW_HORIZON_SETTINGS };
  if (description) cfg.projectName = description;
  return applyAssetType(cfg, systemType);
}

/**
 * An asset's name as the screen shows it. The engine-made default names
 * ("Asset 1", "Asset 2", ...) are stored in English, so a Greek screen read
 * "Μονάδα: Asset 1"; they are shown as "Μονάδα 1". A name the user typed is
 * shown as typed, and nothing stored changes.
 */
export function assetDisplayName(name, greek) {
  const text = name == null ? "" : String(name);
  if (!greek) return text;
  const match = /^Asset (\d+)$/.exec(text.trim());
  return match ? `Μονάδα ${match[1]}` : text;
}

/** A new single-asset portfolio for a brand-new project. */
export function freshPortfolio(assetName, description) {
  const id = newAssetId();
  return {
    schemaVersion: PORTFOLIO_SCHEMA_VERSION,
    project: { ...emptyProjectMeta(), description: description || "" },
    assets: [{ id, name: assetName || "Asset 1", status: "study", notes: "", config: null }],
    activeAssetId: id,
  };
}

/**
 * Split a stored project config into the active asset's flat config (`root`)
 * and the portfolio. Legacy configs (no `portfolio`) become a one-asset
 * portfolio with the same identity as the canonical legacy database asset.
 */
export function splitPortfolio(storedConfig, projectName) {
  const { portfolio: stored, ...root } = storedConfig || {};
  if (stored == null) {
    // Legacy single-asset project: the description it carried becomes the
    // project's description; the asset itself gets a neutral name.
    const p = {
      schemaVersion: PORTFOLIO_SCHEMA_VERSION,
      project: { ...emptyProjectMeta(), description: root.projectName && root.projectName !== "Untitled Project" ? root.projectName : "" },
      assets: [{ id: LEGACY_ASSET_ID, name: "Asset 1", status: null, requiresReview: true, notes: "", config: null }],
      activeAssetId: LEGACY_ASSET_ID,
    };
    return { root, portfolio: p };
  }
  const isConfig = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
  if (!isConfig(stored) || stored.schemaVersion !== PORTFOLIO_SCHEMA_VERSION || !Array.isArray(stored.assets) || !stored.assets.length ||
      !stored.assets.every((a) => isConfig(a) && typeof a.id === "string" && a.id.trim() === a.id && a.id.length) ||
      new Set(stored.assets.map((a) => a.id)).size !== stored.assets.length ||
      !stored.assets.some((a) => a.id === stored.activeAssetId) ||
      stored.assets.some((a) => a.id === stored.activeAssetId ? a.config != null :
        (!isConfig(a.config) || Object.prototype.hasOwnProperty.call(a.config, "portfolio")))) {
    throw new Error("This project's saved asset configuration is incomplete. Reload the project or contact support before saving.");
  }
  const assets = stored.assets.map((a, i) => ({
    ...a,
    id: a.id,
    name: a.name || a.config?.projectName || `Asset ${i + 1}`,
    status: ASSET_STATUSES.includes(normalizeAssetStatus(a.status)) ? normalizeAssetStatus(a.status) : null,
    notes: a.notes || "",
    config: a.config ?? null,
  }));
  const activeAssetId = stored.activeAssetId;
  const portfolio = {
    ...stored,
    schemaVersion: PORTFOLIO_SCHEMA_VERSION,
    project: { ...emptyProjectMeta(), ...(stored.project || {}) },
    assets: assets.map((a) => (a.id === activeAssetId ? { ...a, config: null } : a)),
    activeAssetId,
  };
  return { root, portfolio };
}

/** Accept server normalization while retaining edits made after a save began. */
export function reconcileSavedPortfolio(submitted, current, saved) {
  // A stale client may have sent a random legacy ID. Only remap when the
  // one-to-one identity replacement is unambiguous; keep locally added assets.
  if (submitted.portfolio.assets.length === 1 && saved.portfolio.assets.length === 1) {
    const oldId = submitted.portfolio.assets[0].id;
    const canonicalId = saved.portfolio.assets[0].id;
    if (oldId !== canonicalId && !current.portfolio.assets.some((a) => a.id === canonicalId)) {
      const remap = (state) => ({ ...state, portfolio: {
        ...state.portfolio,
        activeAssetId: state.portfolio.activeAssetId === oldId ? canonicalId : state.portfolio.activeAssetId,
        assets: state.portfolio.assets.map((a) => a.id === oldId ? { ...a, id: canonicalId } : a),
      } });
      submitted = remap(submitted);
      current = remap(current);
    }
  }
  const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const isObject = (value) => value != null && typeof value === "object" && !Array.isArray(value);
  const merge = (before, local, remote) => {
    if (equal(before, local)) return remote;
    if (isObject(before) && isObject(local) && isObject(remote)) {
      return Object.fromEntries([...new Set([...Object.keys(before), ...Object.keys(local), ...Object.keys(remote)])]
        .map((key) => [key, merge(before[key], local[key], remote[key])])
        .filter(([, value]) => value !== undefined));
    }
    return local;
  };
  const beforeAssets = materialiseAssets(submitted.portfolio, submitted.root);
  const localAssets = materialiseAssets(current.portfolio, current.root);
  const remoteAssets = materialiseAssets(saved.portfolio, saved.root);
  const merged = localAssets.map((local) => {
    const before = beforeAssets.find((a) => a.id === local.id);
    const remote = remoteAssets.find((a) => a.id === local.id);
    return before && remote ? merge(before, local, remote) : local;
  });
  remoteAssets.forEach((remote) => {
    if (!beforeAssets.some((a) => a.id === remote.id) && !merged.some((a) => a.id === remote.id)) merged.push(remote);
  });
  const activeAssetId = current.portfolio.activeAssetId === submitted.portfolio.activeAssetId
    ? saved.portfolio.activeAssetId : current.portfolio.activeAssetId;
  const active = merged.find((a) => a.id === activeAssetId);
  return {
    root: active?.config || current.root,
    portfolio: {
      ...merge(submitted.portfolio, current.portfolio, saved.portfolio),
      activeAssetId,
      assets: merged.map(({ active: ignored, ...a }) => ({ ...a, config: a.id === activeAssetId ? null : a.config })),
    },
  };
}

/**
 * What gets persisted: the active asset flat at the root, the portfolio
 * beside it. The project description is written into every asset's
 * `projectName`, which is the field the backend and the reports read.
 */
export function composeForPersistence(rootConfig, portfolio) {
  if (!portfolio) return rootConfig;
  const description = (portfolio.project?.description || "").trim();
  const withDescription = (cfg) => (cfg && description ? { ...cfg, projectName: description } : cfg);
  return {
    ...withDescription(rootConfig),
    portfolio: {
      ...portfolio,
      assets: portfolio.assets.map((a) => (a.id === portfolio.activeAssetId ? { ...a, config: null } : { ...a, config: withDescription(a.config) })),
    },
  };
}

/** Every asset with its config filled in (the active one from `present`). */
export function materialiseAssets(portfolio, present) {
  if (!portfolio) return [];
  return portfolio.assets.map((a) => ({
    ...a,
    config: a.id === portfolio.activeAssetId ? present || a.config || {} : a.config || {},
    active: a.id === portfolio.activeAssetId,
  }));
}

/** Compact facts about one asset config, for tables and the top bar. */
export function assetSummary(config) {
  const cfg = config || {};
  const type = assetTypeDefinition(cfg.systemType);
  const selected = type?.hasBess && Array.isArray(cfg.bess?.selected) ? cfg.bess.selected : [];
  let bessKw = 0;
  let bessKwh = 0;
  selected.forEach((b) => {
    const count = Number(b.count) || 1;
    bessKw += (Number(b.p_max_d) || 0) * count;
    bessKwh += (Number(b.energy) || 0) * count;
  });
  const pvKw = type?.hasPv ? Number(cfg.pvInstalledCap?.kw) || 0 : 0;
  const windEnabled = Boolean(type?.hasWind);
  return {
    systemType: type?.label || cfg.systemType || "",
    biddingZone: cfg.biddingZone || cfg.market || "",
    timeZone: cfg.timeZone || "",
    address: cfg.address || "",
    lat: cfg.loc_info?.lat,
    lon: cfg.loc_info?.lon,
    pvKw,
    bessMw: Math.round(bessKw / 10) / 100,
    bessMwh: Math.round(bessKwh / 10) / 100,
    // Unrounded, for displays that must read a 15.36 kWh pack as such (the
    // MW figures above round it to 0.02).
    bessKw,
    bessKwh,
    bessModels: selected.length,
    windEnabled,
  };
}

/** Portfolio totals. */
export function portfolioTotals(materialised) {
  return materialised.reduce(
    (acc, a) => {
      const s = assetSummary(a.config);
      acc.pvKw += s.pvKw;
      acc.bessMw += s.bessMw;
      acc.bessMwh += s.bessMwh;
      acc.byStatus[a.status] = (acc.byStatus[a.status] || 0) + 1;
      return acc;
    },
    { pvKw: 0, bessMw: 0, bessMwh: 0, byStatus: {} }
  );
}
